import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, MapPin } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import TlIcon from '../../components/TlIcon';
import './CostHistoryPage.css';

const CostHistoryPage = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return formatTL(amount);
    };

    /* Aracılık modelinde paket başı kesinti bölge/alt-bölge bazında tanımlanabilir; bölge tarifesi
       kesinti taşımıyorsa filo geneli kesinti uygulanır (backend FleetContractTerms.Resolve).
       Bu satırlarda alan boş gelir — "0,00 ₺" yazmak yanlış bilgi olur. */
    const formatPlatformFee = (r) => {
        if (!(r.platformFeePerPackage > 0)) {
            return (r.regionId || r.subRegionId) ? 'Filo geneli kesinti' : 'Kesinti tanımlı değil';
        }
        return `${formatCurrency(r.platformFeePerPackage)} + %${r.platformFeeVatRate ?? 20} KDV`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COST_HISTORY);
            if (res.success) {
                setRecords(res.data || []);
            }
        } catch {
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const activeRecord = records.find(r => r.isActive);

    return (
        <div className="ch-page">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><TlIcon size={20} /></div>
                    <div className="page-title">
                        <h1>Fiyatlandırma</h1>
                        <p>Platform tarafından belirlenen teslimat ücret tarifeleriniz</p>
                    </div>
                </div>
                <div className="header-right">
                    <button className="btn btn-secondary btn-icon" onClick={fetchRecords}>
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            {activeRecord && (
                <div className="ch-active-card">
                    <div className="ch-active-badge"><CheckCircle size={14} /> Aktif Tarife</div>
                    <div className="ch-active-grid">
                        <div className="ch-active-item">
                            <span className="ch-active-label">Model</span>
                            <span className="ch-active-value">{Number(activeRecord.pricingModel) === 1 ? 'Aracılık' : 'Yeniden Satış'}</span>
                        </div>
                        {Number(activeRecord.pricingModel) === 1 && (
                            <>
                                <div className="ch-active-item">
                                    <span className="ch-active-label">Paket Başı Platform Kesintisi</span>
                                    <span className="ch-active-value">{formatPlatformFee(activeRecord)}</span>
                                </div>
                                <div className="ch-active-item">
                                    <span className="ch-active-label">KDV</span>
                                    <span className="ch-active-value">%{activeRecord.platformFeeVatRate ?? 20}</span>
                                </div>
                            </>
                        )}
                        <div className="ch-active-item">
                            <span className="ch-active-label">Baz Ücret</span>
                            <span className="ch-active-value">{formatCurrency(activeRecord.baseFee)}</span>
                        </div>
                        <div className="ch-active-item">
                            <span className="ch-active-label">Km Başı</span>
                            <span className="ch-active-value">{formatCurrency(activeRecord.costPerKm)}</span>
                        </div>
                        <div className="ch-active-item">
                            <span className="ch-active-label">Bölge</span>
                            <span className="ch-active-value">{activeRecord.subRegionName || activeRecord.regionName || 'Genel'}</span>
                        </div>
                        {activeRecord.minimumDeliveryCost != null && (
                            <div className="ch-active-item">
                                <span className="ch-active-label">Min Tutar</span>
                                <span className="ch-active-value">{formatCurrency(activeRecord.minimumDeliveryCost)}</span>
                            </div>
                        )}
                        {activeRecord.maximumDeliveryCost != null && (
                            <div className="ch-active-item">
                                <span className="ch-active-label">Max Tutar</span>
                                <span className="ch-active-value">{formatCurrency(activeRecord.maximumDeliveryCost)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="ch-table-section">
                <div className="ch-table-header">
                    <h2>Tarife Geçmişi</h2>
                    <span className="ch-record-count">{records.length} kayıt</span>
                </div>

                {loading ? (
                    <div className="page-loading"><div className="spinning ch-spinner-circle"></div></div>
                ) : records.length === 0 ? (
                    <div className="page-empty">
                        <TlIcon size={40} />
                        <p>Henüz tarife kaydı bulunmuyor</p>
                    </div>
                ) : (
                    <>
                        <div className="data-table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Model</th>
                                        <th>Baz Ücret</th>
                                        <th>Km Başı</th>
                                        <th>Min</th>
                                        <th>Max</th>
                                        <th>Bölge</th>
                                        <th>Geçerlilik</th>
                                        <th>Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((r) => (
                                        <tr key={r.id} className={r.isActive ? 'ch-row-active' : ''}>
                                            <td>
                                                {Number(r.pricingModel) === 1 ? (
                                                    <div className="ch-model-cell">
                                                        <span>Aracılık</span>
                                                        <small>{formatPlatformFee(r)}</small>
                                                    </div>
                                                ) : (
                                                    <span>Yeniden Satış</span>
                                                )}
                                            </td>
                                            <td className="ch-mono">{formatCurrency(r.baseFee)}</td>
                                            <td className="ch-mono">{formatCurrency(r.costPerKm)}</td>
                                            <td>{r.minimumDeliveryCost != null ? formatCurrency(r.minimumDeliveryCost) : '-'}</td>
                                            <td>{r.maximumDeliveryCost != null ? formatCurrency(r.maximumDeliveryCost) : '-'}</td>
                                            <td>
                                                <span className="ch-region-tag">
                                                    <MapPin size={12} />
                                                    {r.subRegionName || r.regionName || 'Genel'}
                                                </span>
                                            </td>
                                            <td>{formatDate(r.validFrom)}</td>
                                            <td>
                                                <span className={`ch-status ${r.isActive ? 'active' : 'passive'}`}>
                                                    {r.isActive ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="ch-cards-mobile">
                            {records.map((r) => (
                                <div key={r.id} className={`ch-card ${r.isActive ? 'ch-card-active' : ''}`}>
                                    <div className="ch-card-top">
                                        <span className={`ch-status ${r.isActive ? 'active' : 'passive'}`}>
                                            {r.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </div>
                                    <div className="ch-card-grid">
                                        <div><span>Model</span><strong>{Number(r.pricingModel) === 1 ? 'Aracılık' : 'Yeniden Satış'}</strong></div>
                                        {Number(r.pricingModel) === 1 && (
                                            <div><span>Paket Başı Kesinti</span><strong>{formatPlatformFee(r)}</strong></div>
                                        )}
                                        <div><span>Baz Ücret</span><strong>{formatCurrency(r.baseFee)}</strong></div>
                                        <div><span>Km Başı</span><strong>{formatCurrency(r.costPerKm)}</strong></div>
                                        <div><span>Bölge</span><strong>{r.subRegionName || r.regionName || 'Genel'}</strong></div>
                                    </div>
                                    <div className="ch-card-footer">{formatDate(r.validFrom)}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CostHistoryPage;
